import { Component, OnInit, HostListener } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from './services/auth.service';
import { UserRole } from './models/auth.model';

interface MenuItem {
  labelKey: string;
  icon: string;
  route?: string;
  roles: UserRole[];
  children?: MenuItem[];
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, TranslateModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  sidebarVisible: boolean = true;
  showSidebar: boolean = true;
  currentLanguage: string = 'en';
  languageDropdownVisible: boolean = false;
  currentUserName: string = '';
  expandedMenuGroups: Set<string> = new Set();
  activePopoverGroup: string | null = null;
  popoverTop: number = 0;

  languages = [
    { code: 'en', name: 'English', flagCode: 'gb' },
    { code: 'ru', name: 'Русский', flagCode: 'ru' },
    { code: 'lv', name: 'Latviešu', flagCode: 'lv' },
    { code: 'tr', name: 'Türkçe', flagCode: 'tr' },
    { code: 'az', name: 'Azərbaycan', flagCode: 'az' }
  ];

  private allMenuItems: MenuItem[] = [
    { labelKey: 'MENU.HOME', icon: 'pi pi-home', route: '/home', roles: [] },
    {
      labelKey: 'MENU.RESTAURANT',
      icon: 'pi pi-building',
      roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager],
      children: [
        { labelKey: 'MENU.ALL_RESTAURANTS', icon: 'pi pi-list', route: '/all-restaurants', roles: [UserRole.SuperAdmin] },
        { labelKey: 'MENU.MY_RESTAURANTS', icon: 'pi pi-list', route: '/my-restaurants', roles: [UserRole.RestaurantOwner] },
        { labelKey: 'MENU.TABLES', icon: 'pi pi-th-large', route: '/tables', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] },
        { labelKey: 'MENU.RESTAURANT_MENU_ITEMS', icon: 'pi pi-shopping-bag', route: '/restaurant-menu-items', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] },
        { labelKey: 'MENU.RESTAURANT_MENU_ITEMS_PRICES', icon: 'pi pi-dollar', route: '/restaurant-menu-items-prices', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] },
        { labelKey: 'MENU.CATEGORIES', icon: 'pi pi-tags', route: '/categories', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] },
        { labelKey: 'MENU.DISCOUNTS', icon: 'pi pi-tag', route: '/discounts', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] },
        { labelKey: 'MENU.RESTAURANT_USERS', icon: 'pi pi-users', route: '/restaurant-users', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
      ]
    },
    {
      labelKey: 'MENU.BRANCHES',
      icon: 'pi pi-map-marker',
      roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager],
      children: [
        { labelKey: 'MENU.ALL_BRANCHES', icon: 'pi pi-list', route: '/all-branches', roles: [UserRole.SuperAdmin] },
        { labelKey: 'MENU.MY_BRANCHES', icon: 'pi pi-list', route: '/my-branches', roles: [UserRole.RestaurantOwner] },
        { labelKey: 'MENU.MENUS', icon: 'pi pi-book', route: '/branch-menu-items', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] }
      ]
    },
    {
      labelKey: 'MENU.ACTIVITIES',
      icon: 'pi pi-stopwatch',
      roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier],
      children: [
        { labelKey: 'MENU.ACTIVITY_BOARD', icon: 'pi pi-th-large', route: '/activity-board', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.ACTIVITY_TYPES', icon: 'pi pi-tags', route: '/activity-types', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] },
        { labelKey: 'MENU.ACTIVITIES_LIST', icon: 'pi pi-list', route: '/activities', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
      ]
    },
    {
      labelKey: 'MENU.REPORTING',
      icon: 'pi pi-chart-bar',
      roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier],
      children: [
        { labelKey: 'MENU.SALES', icon: 'pi pi-dollar', route: '/reports/sales', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.BRANCH_COMPARISON', icon: 'pi pi-chart-line', route: '/reports/branch-comparison', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] },
        { labelKey: 'MENU.TABLE_UTILIZATION', icon: 'pi pi-th-large', route: '/reports/table-utilization', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.CANCELLATIONS', icon: 'pi pi-times-circle', route: '/reports/cancellations', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.WEEKLY_PERFORMANCE', icon: 'pi pi-calendar', route: '/reports/weekly-performance', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        // { labelKey: 'MENU.CUSTOMER_LIFETIME_VALUE', icon: 'pi pi-heart', route: '/reports/customer-lifetime-value', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.MENU_ITEM_TRENDS', icon: 'pi pi-chart-line', route: '/reports/menu-item-trends', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.MONTHLY_REVIEW', icon: 'pi pi-calendar-plus', route: '/reports/monthly-review', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        { labelKey: 'MENU.CUSTOM_BUILDER', icon: 'pi pi-sliders-h', route: '/reports/custom-builder', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] },
        // { labelKey: 'MENU.PREDICTIVE_ANALYTICS', icon: 'pi pi-chart-scatter', route: '/reports/predictive-analytics', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] },
        { labelKey: 'MENU.BENCHMARKING', icon: 'pi pi-gauge', route: '/reports/benchmarking', roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
      ]
    }
  ];

  get menuItems(): MenuItem[] {
    return this.allMenuItems.filter(item => this.hasAccess(item)).map(item => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter(child => this.hasAccess(child))
        };
      }
      return item;
    });
  }

  private hasAccess(item: MenuItem): boolean {
    if (item.roles.length === 0) {
      return true; // No role restriction
    }
    return this.authService.hasRole(item.roles);
  }

  toggleMenuGroup(labelKey: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.sidebarVisible) {
      // Collapsed mode: toggle flyout popover
      if (this.activePopoverGroup === labelKey) {
        this.activePopoverGroup = null;
      } else {
        const target = event?.currentTarget as HTMLElement;
        if (target) {
          this.popoverTop = target.getBoundingClientRect().top;
        }
        this.activePopoverGroup = labelKey;
      }
      return;
    }

    // Expanded mode: toggle inline submenu
    if (this.expandedMenuGroups.has(labelKey)) {
      this.expandedMenuGroups.delete(labelKey);
    } else {
      this.expandedMenuGroups.add(labelKey);
    }
  }

  closeFlyout(): void {
    this.activePopoverGroup = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.activePopoverGroup = null;
  }

  isMenuGroupExpanded(labelKey: string): boolean {
    return this.expandedMenuGroups.has(labelKey);
  }

  constructor(
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService
  ) {
    // Initialize translation
    this.translate.addLangs(['en', 'ru', 'lv', 'tr', 'az']);
    this.translate.setDefaultLang('en');

    // Get saved language or use default
    const savedLang = localStorage.getItem('language') || 'en';
    this.currentLanguage = savedLang;
    this.translate.use(savedLang);

    // Hide sidebar on login page and sync language
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.showSidebar = !event.url.includes('/login');

      // Re-check language from localStorage when navigating (especially after login)
      const savedLang = localStorage.getItem('language') || 'en';
      if (this.currentLanguage !== savedLang) {
        this.currentLanguage = savedLang;
        this.translate.use(savedLang);
      }
    });
  }

  ngOnInit() {
    // Subscribe to auth state changes
    this.authService.authState$.subscribe(state => {
      if (state.user) {
        this.currentUserName = state.user.fullName;
      } else {
        this.currentUserName = '';
      }
    });
  }

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;

    // Collapse all expanded menu groups when closing the sidebar
    if (!this.sidebarVisible) {
      this.expandedMenuGroups.clear();
    }
    this.activePopoverGroup = null;
  }

  toggleLanguageDropdown() {
    this.languageDropdownVisible = !this.languageDropdownVisible;
  }

  changeLanguage(langCode: string) {
    this.currentLanguage = langCode;
    this.translate.use(langCode);
    localStorage.setItem('language', langCode);
    this.languageDropdownVisible = false;
  }

  getCurrentLanguage() {
    return this.languages.find(lang => lang.code === this.currentLanguage);
  }

  logout() {
    this.authService.logout();
  }
}
